import ActivityCategory from "../../entities/activityCategory";

/**
 * ActivityCategory actions
 *
 * Call order:
 *
 * ACTIVITYCATEGORY_SAVE_QUEUE:	adds the item to the save queue
 * --> ACTIVITYCATEGORY_SAVE:	modifies the state
 * --> ACTIVITYCATEGORY_SAVE_SUCCESS: if all went well
 * --> ACTIVITYCATEGORY_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail ACTIVITYCATEGORY_SAVE_QUEUE is during the call to ACTIVITYCATEGORY_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at ACTIVITYCATEGORY_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: ACTIVITYCATEGORY_SAVE_SUCCESS, ACTIVITYCATEGORY_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or ACTIVITYCATEGORY_PENDING_CHECK (it left the queue).
 */

export const ACTIVITYCATEGORY_FETCH_ONE_QUEUE: any = 'ACTIVITYCATEGORY_FETCH_ONE_QUEUE';
export const ACTIVITYCATEGORY_FETCH_SUCCESS: any = 'ACTIVITYCATEGORY_FETCH_SUCCESS';
export const ACTIVITYCATEGORY_FETCH_FAILURE: any = 'ACTIVITYCATEGORY_FETCH_FAILURE';
export const ACTIVITYCATEGORY_FETCH_ONE_SUCCESS: any = 'ACTIVITYCATEGORY_FETCH_ONE_SUCCESS';
export const ACTIVITYCATEGORY_FETCH_ONE_FAILURE: any = 'ACTIVITYCATEGORY_FETCH_ONE_FAILURE';

export const ACTIVITYCATEGORY_SAVE: any = 'ACTIVITYCATEGORY_SAVE';
export const ACTIVITYCATEGORY_SAVE_QUEUE: any = 'ACTIVITYCATEGORY_SAVE_QUEUE';
export const ACTIVITYCATEGORY_SAVE_SUCCESS: any = 'ACTIVITYCATEGORY_SAVE_SUCCESS';
export const ACTIVITYCATEGORY_SAVE_FAILURE: any = 'ACTIVITYCATEGORY_SAVE_FAILURE';

export const ACTIVITYCATEGORY_SAVE_MANY: any = 'ACTIVITYCATEGORY_SAVE_MANY';
export const ACTIVITYCATEGORY_SAVE_MANY_QUEUE: any = 'ACTIVITYCATEGORY_SAVE_MANY_QUEUE';
export const ACTIVITYCATEGORY_SAVE_MANY_SUCCESS: any = 'ACTIVITYCATEGORY_SAVE_MANY_SUCCESS';
export const ACTIVITYCATEGORY_SAVE_MANY_FAILURE: any = 'ACTIVITYCATEGORY_SAVE_MANY_FAILURE';

export const ACTIVITYCATEGORY_REMOVE: any = 'ACTIVITYCATEGORY_REMOVE';
export const ACTIVITYCATEGORY_REMOVE_QUEUE: any = 'ACTIVITYCATEGORY_REMOVE_QUEUE';
export const ACTIVITYCATEGORY_REMOVE_SUCCESS: any = 'ACTIVITYCATEGORY_REMOVE_SUCCESS';
export const ACTIVITYCATEGORY_REMOVE_FAILURE: any = 'ACTIVITYCATEGORY_REMOVE_FAILURE';

export const ACTIVITYCATEGORY_RESTORE: any = 'ACTIVITYCATEGORY_RESTORE';
export const ACTIVITYCATEGORY_RESTORE_QUEUE: any = 'ACTIVITYCATEGORY_RESTORE_QUEUE';
export const ACTIVITYCATEGORY_RESTORE_SUCCESS: any = 'ACTIVITYCATEGORY_RESTORE_SUCCESS';
export const ACTIVITYCATEGORY_RESTORE_FAILURE: any = 'ACTIVITYCATEGORY_RESTORE_FAILURE';

export const ACTIVITYCATEGORY_RESTORE_MANY: any = 'ACTIVITYCATEGORY_RESTORE_MANY';
export const ACTIVITYCATEGORY_REMOVE_MANY: any = 'ACTIVITYCATEGORY_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const ACTIVITYCATEGORY_SAVE_REVERT: any = 'ACTIVITYCATEGORY_SAVE_REVERT';
export const ACTIVITYCATEGORY_PENDING_CHECK: any = 'ACTIVITYCATEGORY_PENDING_CHECK';

/* Fetch actions */
export const fetchActivityCategorySuccess = (activityCategories: ActivityCategory[], partial: boolean, lastFetch: number) => ({type: ACTIVITYCATEGORY_FETCH_SUCCESS, activityCategories, partial, lastFetch});
export const fetchActivityCategoryFailure = (err: any) => {
		console.error('ActivityCategoryAction: fetchActivityCategoryFailure', err);
		return {type: ACTIVITYCATEGORY_FETCH_FAILURE, err};
};
export const fetchOneActivityCategoryQueue = (activityCategoryId: string, sentAddedOn?: number) => ({type: ACTIVITYCATEGORY_FETCH_ONE_QUEUE, activityCategoryId, sentAddedOn});
export const fetchOneActivityCategorySuccess = (activityCategory: ActivityCategory | null, sentAddedOn?: number, activityCategoryId: string = activityCategory?.id) => ({type: ACTIVITYCATEGORY_FETCH_ONE_SUCCESS, activityCategoryId, activityCategory, sentAddedOn});
export const fetchOneActivityCategoryFailure = (id: string, err: any) => {
		console.error('ActivityCategoryAction: fetchOneActivityCategoryFailure', err);
		return {type: ACTIVITYCATEGORY_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveActivityCategory = (activityCategory: ActivityCategory) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE action', activityCategory?.id);
    return {type: ACTIVITYCATEGORY_SAVE, activityCategory};
}
export const saveActivityCategoryQueue = (activityCategory: ActivityCategory, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_QUEUE action', activityCategory?.id);
    return {type: ACTIVITYCATEGORY_SAVE_QUEUE, activityCategory, onQueued};
}
export const saveActivityCategorySuccess = (activityCategory: ActivityCategory, sentAddedOn?: number) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_SUCCESS action', activityCategory?.id);
    return {type: ACTIVITYCATEGORY_SAVE_SUCCESS, activityCategory, sentAddedOn};
}
export const saveActivityCategoryFailure = (activityCategory: ActivityCategory, status: number, message: string) => {
    console.warn(`⮂ ACTIVITYCATEGORY_SAVE_FAILURE error saving activityCategory: HTTP ${status}: ${message}`, activityCategory?.id);
    return {type: ACTIVITYCATEGORY_SAVE_FAILURE, activityCategory, status, message};
};

/* SaveMany actions */
export const saveManyActivityCategories = (activityCategories: ActivityCategory[]) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY action', activityCategories?.map(activityCategory => activityCategory?.id).join(','));
    return {type: ACTIVITYCATEGORY_SAVE_MANY, activityCategories};
}
export const saveManyActivityCategoriesQueue = (activityCategories: ActivityCategory[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY_QUEUE action', activityCategories?.map(activityCategory => activityCategory.id).join(','));
    return {type: ACTIVITYCATEGORY_SAVE_MANY_QUEUE, activityCategories, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `activityCategories` (OWA-1018) */
export const saveManyActivityCategoriesSuccess = (activityCategories: ActivityCategory[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY_SUCCESS action', activityCategories?.map(activityCategory => activityCategory.id).join(','));
    return {type: ACTIVITYCATEGORY_SAVE_MANY_SUCCESS, activityCategories, sentAddedOns};
}
export const saveManyActivityCategoriesFailure = (activityCategories: ActivityCategory[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_ACTIVITYCATEGORIES_FAILURE Error saving activityCategories: HTTP ${status}: ${message}`, activityCategories?.map(activityCategory => activityCategory.id).join(','));
		return {type: ACTIVITYCATEGORY_SAVE_MANY_FAILURE, activityCategories, status, message};
};

/* Remove actions */
export const removeActivityCategory = (activityCategory: ActivityCategory) => ({ type: ACTIVITYCATEGORY_REMOVE, activityCategory });
export const removeActivityCategoryQueue = (activityCategory: ActivityCategory) => ({ type: ACTIVITYCATEGORY_REMOVE_QUEUE, activityCategory });
export const removeActivityCategorySuccess = (activityCategory: ActivityCategory) => ({ type: ACTIVITYCATEGORY_REMOVE_SUCCESS, activityCategory });
export const removeActivityCategoryFailure = (activityCategory: ActivityCategory, status: number, message: string) => {
	console.error(`Redux: removeActivityCategoryFailure Error removing activityCategory: HTTP ${status}: ${message}`);
	return ({ type: ACTIVITYCATEGORY_REMOVE_FAILURE, activityCategory, status, message });
};

/* Restore actions */
export const restoreActivityCategory = (activityCategory: ActivityCategory) => ({ type: ACTIVITYCATEGORY_RESTORE, activityCategory });
export const restoreActivityCategoryQueue = (activityCategory: ActivityCategory) => ({ type: ACTIVITYCATEGORY_RESTORE_QUEUE, activityCategory });
export const restoreActivityCategorySuccess = (activityCategory: ActivityCategory, sentAddedOn?: number) => ({ type: ACTIVITYCATEGORY_RESTORE_SUCCESS, activityCategory, sentAddedOn });
export const restoreActivityCategoryFailure = (activityCategory: ActivityCategory, status: number, message: string) => {
	console.error(`Redux: restoreActivityCategoryFailure Error restoring activityCategory: HTTP ${status}: ${message}`);
	return ({ type: ACTIVITYCATEGORY_RESTORE_FAILURE, activityCategory, status, message });
};

/* RestoreMany actions */
export const restoreManyActivityCategories = (activityCategories: ActivityCategory[]) => ({ type: ACTIVITYCATEGORY_RESTORE_MANY, activityCategories });

/* RemoveMany actions */
export const removeManyActivityCategories = (activityCategories: ActivityCategory[]) => ({ type: ACTIVITYCATEGORY_REMOVE_MANY, activityCategories });

/* Pending save actions (OWA-1018) */
/**
 * The save `activityCategory` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertActivityCategorySave = (activityCategory: ActivityCategory, options: {sentAddedOn?: number, current?: ActivityCategory | null, keep?: boolean} = {}) =>
    ({type: ACTIVITYCATEGORY_SAVE_REVERT, activityCategory, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkActivityCategoryPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: ACTIVITYCATEGORY_PENDING_CHECK, live, refused, readAt});